<script>
    import ViewLogs from "../ViewLogs.svelte";
    import ViewNotes from "../ViewNotes.svelte";
    import PeopleChooser from "../PeopleChooser.svelte";
    import CompanyChooser from "../CompanyChooser.svelte";
    import {PAGE_ID, router, navigateTo} from "../../stores/router";
    import {
        app_state,
        displayRequest,
        selectRequest,
        selectPerson,
        updateRequest,
        fullNameFromRawPerson,
        selectCompany,
        filemanager
    } from "../../stores/misc";
    import {
        I_PERSON,
        I_COMPANY,
        I_REQUEST,
        getNullRequest,
        requestAsObject,
        REQUEST_LABELS,
        REQUEST_EXPLANATION, DATATYPE
    } from "../../../../../../lib/datatypes";
    import {deepCopyByValue, prettyHTMLFromPlainText} from "../../../../../../lib/utils";
    import {people} from "../../stores/people";
    import {getTimeDiffAsShortText, now, toDate} from "../../../../../../lib/dates_times";
    import Label from "../Label.svelte";
    import {companies} from "../../stores/companies";
    import RelatedPeopleList from "../RelatedPeopleList.svelte";
    import RelatedCompaniesList from "../RelatedCompaniesList.svelte";
    import FileAttachmentList from "../FileAttachmentList.svelte";
    import NextAction from "../NextAction.svelte";


    // 'Empty' display person for use when there is no selected person
    let empty_request_obj = requestAsObject(getNullRequest());

    $: selected_request = $app_state.selected_request_id === null ? empty_request_obj : displayRequest($app_state.selected_request_id);

    $: related_people = selected_request !== null ? selected_request.related.filter(row => row.type === DATATYPE.PERSON) : [];
    $: related_companies = selected_request !== null ? selected_request.related.filter(row => row.type === DATATYPE.COMPANY) : [];

    let edited_selected_request = null;
    let notes = [];
    let logs = [];
    const resetStateParams = () => {
        edited_selected_request = null;
        notes = [];
        logs = [];
    };
    const loadNotesAndLogs = async () => {
        const response = await fetch(`/api/request/${$app_state.selected_request_id}`);
        const result = await response.json();
        notes = result.notes;
        logs = result.log;
    };
    $: (async () => {
        if($app_state.selected_request_id === null) {
            resetStateParams();
        } else {
            await loadNotesAndLogs();
        }
    })()

    const VIEW_STATE = {
        VIEWING: 0,
        EDITING: 1
    };
    let view_state = VIEW_STATE.VIEWING;

    const TAB_STATE = {
        NOTES: 1,
        LOG: 2
    };
    let tab_state = TAB_STATE.NOTES;

    const onEditRequestClick = async () => {
        edited_selected_request = await deepCopyByValue(selected_request);
        // console.log(edited_selected_request);
        view_state = VIEW_STATE.EDITING;
    };

    $: edited_related_people = edited_selected_request !== null ? edited_selected_request.related.filter(row => row.type === DATATYPE.PERSON) : [];
    $: edited_related_companies = edited_selected_request !== null ? edited_selected_request.related.filter(row => row.type === DATATYPE.COMPANY) : [];

    let show_related_person_search = false;
    let show_related_company_search = false;

    let person_being_edited = false;
    const onEditPersonClick = () => {
        person_being_edited = true;
    }

    const onPersonClick = person_id => {
        if(edited_selected_request !== null) {
            edited_selected_request.id_person = person_id;
            // edited_selected_request.positions[person_being_edited].company_name = $companies.lookup[company_id][I_COMPANY.NAME];
            person_being_edited = false;
        }
        else {
            console.warn(`no request being edited, why was this person clicked?`);
        }
    };

    const onRelatedClick = (type, id) => {
        if(edited_selected_request !== null) {
            edited_selected_request.related.push({type: type, id:id});
            edited_selected_request = edited_selected_request;
        }
        else {
            console.warn(`no request being edited, why was this person/company clicked?`);
        }
        show_related_person_search = false;
        show_related_company_search = false;
    };
    const resetEditingRequest = () => {
        view_state = VIEW_STATE.VIEWING;
        edited_selected_request = null;
    }
    const saveEditedRequest = async () => {
        if(edited_selected_request === null) console.warn('Trying to save a null edited person!');
        let saved_request = await updateRequest(edited_selected_request);
        selectRequest(saved_request[I_REQUEST.ID]); // ..to reflect the changes made (otherwise doesn't pick it up)
        resetEditingRequest();
    };

    const onNextActionUpdated = async (e) => {
        const new_next_action = e.detail.new_next_action;
        // NB taking a full copy of the request and then going through the existing saveEditedRequest path..
        edited_selected_request = await deepCopyByValue(selected_request);
        edited_selected_request.next_action = new_next_action;
        await saveEditedRequest();
    };

    let is_uploading_files = false;
    const doChooseFile = () => {
        filemanager.chooseFiles();
        is_uploading_files = true;
    };
    $: {
        if (is_uploading_files) {
            if ($filemanager.files.length > 0) {
                if (edited_selected_request !== null) {
                    edited_selected_request.files = edited_selected_request.files.concat($filemanager.files);
                    edited_selected_request = edited_selected_request;
                    is_uploading_files = false;
                    filemanager.resetFiles();
                    console.log('files added, full list =', edited_selected_request.files);
                }
            }
        }
    }


    const onBackClick = () => {
        if($router.context.previous_page_id !== null) {
            navigateTo($router.context.previous_page_id);
        }
        else {
            navigateTo(PAGE_ID.REQUESTS);
        }
    };

</script>


<div class="absolute top-16 right-0 bottom-0 left-0 overflow-y-auto" class:hidden={$router.current_page !== PAGE_ID.VIEW_REQUEST}>

    <!-- Header -->
    <div class="px-4 sm:px-6 lg:px-8">

        <!--<Breadcrumbs></Breadcrumbs>-->

        <div class="h-16 flex items-center justify-between">
            <a on:click|preventDefault={onBackClick} href="/" class="flex items-center text-sm font-medium text-gray-500 hover:text-gray-700">
                <svg class="-ml-1 mr-1 h-5 w-5 flex-shrink-0 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fill-rule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clip-rule="evenodd" />
                </svg>
                Back
            </a>
            <div class="flex flex-shrink-0 md:mt-0 md:ml-4">
                <button on:click={onEditRequestClick}
                        type="button" class="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">Edit</button>
                <!--<button type="button" class="ml-3 inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">Publish</button>-->
            </div>
        </div>

    </div>

    <main class="pb-10">

        {#if view_state === VIEW_STATE.VIEWING}

        <div class="px-4 sm:px-6 lg:px-8">

            <div class="flex justify-between">
                <!-- Request pill-->
                <div class="flex inline-flex items-center px-2 py-1 rounded-full ring-1 ring-inset ring-gray-300">
                    <svg class="h-4 w-4 text-yellow-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM8.94 6.94a.75.75 0 11-1.061-1.061 3 3 0 112.871 5.026v.345a.75.75 0 01-1.5 0v-.5c0-.72.57-1.172 1.081-1.287A1.5 1.5 0 108.94 6.94zM10 15a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
                    </svg>
                    <p class="ml-1 text-sm uppercase font-light text-gray-600">Request</p>
                </div>

                <!-- Updated date -->
                <div class="flex">
                    <div class="flex items-center text-sm text-gray-500">
                        <svg class="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd" d="M5.75 2a.75.75 0 01.75.75V4h7V2.75a.75.75 0 011.5 0V4h.25A2.75 2.75 0 0118 6.75v8.5A2.75 2.75 0 0115.25 18H4.75A2.75 2.75 0 012 15.25v-8.5A2.75 2.75 0 014.75 4H5V2.75A.75.75 0 015.75 2zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75z" clip-rule="evenodd" />
                        </svg>
                        <p>Updated
                            <time datetime="{selected_request.updated}">{getTimeDiffAsShortText(now().valueOf() - toDate(selected_request.updated))}</time>
                        </p>
                    </div>
                </div>
            </div>

            <div class="mt-4 flex items-baseline">
                <p class="text-sm text-gray-500 mr-2 align-baseline">From:</p>
                <span class="inline-flex align-text-bottom"><Label type={'person'}></Label></span>
                <h1
                        on:click={() => {
                            selectPerson(selected_request.id_person);
                            navigateTo(PAGE_ID.VIEW_PERSON, PAGE_ID.VIEW_REQUEST);
                        }}
                        class="ml-2 text-lg font-medium text-gray-900 hover:text-indigo-500 cursor-pointer">
                    {selected_request.id_person === '' ? '' : fullNameFromRawPerson($people.lookup[selected_request.id_person])}
                </h1>
            </div>

            <p class="mt-4 font-semibold text-gray-900">
                {@html selected_request.title}
            </p>
            <p class="mt-4 text-gray-900">
                {@html prettyHTMLFromPlainText(selected_request.description)}
            </p>

            <NextAction type="request" owner_id={selected_request.id} next_action={selected_request.next_action}
                        on:nextActionUpdate={onNextActionUpdated}
                        on:logsChanged={loadNotesAndLogs}></NextAction>

            {#if related_people.length > 0}
<!--                <p class="mt-4 text-sm text-gray-500">Related People:</p>-->
                <RelatedPeopleList related_people={related_people} return_page_id={PAGE_ID.VIEW_REQUEST}></RelatedPeopleList>
            {/if}

            {#if related_companies.length > 0}
<!--                <p class="mt-4 text-sm text-gray-500">Related Companies:</p>-->
                <RelatedCompaniesList related_companies={related_companies} return_page_id={PAGE_ID.VIEW_REQUEST}></RelatedCompaniesList>
            {/if}

            {#if selected_request.files.length > 0}
                <div class="my-4">
<!--                    <p class="mt-4 text-sm text-gray-500">Files:</p>-->
                    <FileAttachmentList files={selected_request.files}></FileAttachmentList>
                </div>
            {/if}

        </div>

        {/if}
        {#if view_state === VIEW_STATE.EDITING}

        <div class="px-4 sm:px-6 lg:px-8">

            <p class="text-sm font-medium text-gray-600">{@html REQUEST_EXPLANATION}</p>

            <div class="mt-4">
                <label for="person" class="block text-sm font-medium text-gray-900">Person</label>
                {#if person_being_edited}
                    <PeopleChooser
                            type="box"
                            current_search_str=''
                            on:personClicked={(e) => onPersonClick(e.detail.id)}
                            on:cancelClicked={() => person_being_edited = false}></PeopleChooser>
                {:else}
                    <input on:focus={() => onEditPersonClick()} value="{edited_selected_request.id_person === '' ? '' : `${fullNameFromRawPerson($people.lookup[edited_selected_request.id_person])}`}" type="text" name="person" id="person" class="mt-1 block w-full rounded-md border-gray-300 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
                {/if}
            </div>

            <div class="mt-4">
                <label for="request-title" class="block text-sm font-medium text-gray-900">Title</label>
                <p class="text-sm italic text-gray-500">({@html REQUEST_LABELS.TITLE})</p>
                <input bind:value="{edited_selected_request.title}" type="text" name="request-title" id="request-title" autocomplete="request-title"
                       class="mt-3 block w-full rounded-md border-gray-300 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
            </div>

            <div class="mt-4">
                <label for="description" class="block text-sm font-medium text-gray-900">Description</label>
                <p class="text-sm italic text-gray-500">({@html REQUEST_LABELS.DESCRIPTION})</p>
                <div class="mt-3">
                    <textarea bind:value={edited_selected_request.description}
                              id="description" name="description" rows="4"
                              class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"></textarea>
                </div>
            </div>

            <div class="mt-4">
                <p class="block text-sm font-medium text-gray-900">Related People</p>
                <RelatedPeopleList related_people={edited_related_people} return_page_id={PAGE_ID.VIEW_REQUEST}></RelatedPeopleList>
                {#if !show_related_person_search}
                <button on:click={() => show_related_person_search = true}
                        type="button"
                        class="mt-2 inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100"
                >Add Related Person</button>
                {:else}
                <PeopleChooser
                        type="box"
                        current_search_str=""
                        minimise_box_without_search={true}
                        allow_adding_people={false}
                        on:personClicked={(e) => onRelatedClick(DATATYPE.PERSON, e.detail.id)}
                        on:cancelClicked={() => show_related_person_search = false}></PeopleChooser>
                {/if}
            </div>

            <div class="mt-4">
                <p class="block text-sm font-medium text-gray-900">Related Companies</p>
                <RelatedCompaniesList related_companies={edited_related_companies} return_page_id={PAGE_ID.VIEW_REQUEST}></RelatedCompaniesList>
                {#if !show_related_company_search}
                    <button on:click={() => show_related_company_search = true}
                            type="button"
                            class="mt-2 inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100"
                    >Add Related Company</button>
                {:else}
                <CompanyChooser
                        type="box"
                        current_search_str=""
                        minimise_box_without_search={true}
                        allow_adding_company={false}
                        on:companyClicked={(e) => onRelatedClick(DATATYPE.COMPANY, e.detail.id)}
                        on:cancelClicked={() => show_related_company_search = false}></CompanyChooser>
                {/if}
            </div>

            <div class="my-4">
                <p class="block text-sm font-medium text-gray-900">Files</p>
                {#if edited_selected_request.files.length > 0}
                    <div class="mt-2">
                        <FileAttachmentList files={edited_selected_request.files}></FileAttachmentList>
                    </div>
                {/if}
                {#if $filemanager.is_uploading}
                    <div class="flex items-center justify-center rounded-md border border-transparent bg-orange-600 px-2 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    ><svg class="animate-spin self-center w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                    </svg>
                        Sending..</div>
                {/if}
                <button on:click={doChooseFile}
                        type="button"
                        class="mt-2 inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100"
                >Add File</button>
            </div>

            <div class="flex justify-end pt-8">
                <button on:click={resetEditingRequest}
                        type="button"
                        class="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-900 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >Cancel</button>
                <button on:click={saveEditedRequest}
                        type="submit"
                        class="ml-3 inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >Save</button>
            </div>

        </div>
        {/if}

        <div class="block mt-6">
            <div class="border-b border-gray-200">
                <nav class="-mb-px px-4 flex justify-between sm:justify-start space-x-8" aria-label="Tabs">
                    <a href="/" on:click|preventDefault={() => tab_state = TAB_STATE.NOTES} class="{tab_state === TAB_STATE.NOTES ? `border-indigo-500 text-indigo-600` : `border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700` } group inline-flex items-center border-b-2 py-4 px-1 text-sm font-medium">
                        <svg class="{tab_state === TAB_STATE.NOTES ? `text-indigo-500` : `text-gray-400 group-hover:text-gray-500`} -ml-0.5 mr-2 h-5 w-5"  fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
                        </svg>
                        <span>Notes</span>
                    </a>

                    <a href="/" on:click|preventDefault={() => tab_state = TAB_STATE.LOG} class="{tab_state === TAB_STATE.LOG ? `border-indigo-500 text-indigo-600` : `border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700` } group inline-flex items-center border-b-2 py-4 px-1 text-sm font-medium" aria-current="page">
                        <svg class="{tab_state === TAB_STATE.LOG ? `text-indigo-500` : `text-gray-400 group-hover:text-gray-500`} -ml-0.5 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                        </svg>
                        <span>Log</span>
                    </a>
                </nav>
            </div>
        </div>

        {#if tab_state === TAB_STATE.NOTES}
        <ViewNotes type="request" owner_id={selected_request.id} bind:notes></ViewNotes>
        {/if}

        {#if tab_state === TAB_STATE.LOG}
        <ViewLogs type="request" owner_id={selected_request.id} bind:logs></ViewLogs>
        {/if}

    </main>

</div>


<style></style>